import { ImageIcon, UserRoundIcon, Building2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import { ForwardRefExoticComponent, useMemo } from "react";
import { LucideProps } from "lucide-react";
import { RefAttributes } from "react";

export interface AvatarProps {
  src?: string;
  initials?: string;
  noImageIcon?: "profile" | "generic" | "company";
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  className?: string;
  primaryText?: string;
  secondaryText?: string;
  descriptionText?: string;
  Icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  /**
   * Opt in to hover feedback (border + icon darkening).
   *
   * Defaults to FALSE. Hover states promise interactivity, and this avatar is usually
   * presentational — e.g. the certificate Scans list and map card, where there is no
   * user page to click through to. Highlighting on hover there invites a click that
   * does nothing. Set this only when the avatar is genuinely wrapped in a link/button.
   */
  interactive?: boolean;
}

export const Avatar = ({
  src,
  initials,
  noImageIcon = "generic",
  size = "md",
  primaryText,
  secondaryText,
  descriptionText,
  Icon,
  interactive = false,
}: AvatarProps) => {
  const {
    noImageIconClass,
    containerClass,
    primaryTextClass,
    secondaryTextClass,
    iconContainerClass,
    iconClass,
    initialsClass,
  } = useMemo(() => {
    switch (size) {
      case "xs":
        return {
          noImageIconClass: "w-[14px] h-[14px]",
          initialsClass: "text-[12px] leading-3",
          containerClass: "w-6 h-6",
          primaryTextClass: "text-[9px] leading-3",
          secondaryTextClass: "text-[7px] leading-3",
          iconContainerClass: "w-4 h-4",
          iconClass: "h-2 w-2",
        };
      case "sm":
        return {
          noImageIconClass: "w-[20px] h-[20px]",
          initialsClass: "text-[14px] leading-5",
          containerClass: "w-8 h-8",
          primaryTextClass: "text-[14px] leading-5",
          secondaryTextClass: "text-[12px] leading-4",
          iconContainerClass: "w-6 h-6",
          iconClass: "h-3 w-3",
        };
      case "md":
        return {
          noImageIconClass: "w-[20px] h-[20px]",
          initialsClass: "text-[16px] leading-5",
          containerClass: "w-10 h-10",
          primaryTextClass: "text-[16px] leading-5",
          secondaryTextClass: "text-[14px] leading-5",
          iconContainerClass: "w-7 h-7",
          iconClass: "h-4 w-4",
        };
      case "lg":
        return {
          noImageIconClass: "w-[22px] h-[22px]",
          initialsClass: "text-[16px] leading-6",
          containerClass: "w-12 h-12",
          primaryTextClass: "text-[18px] leading-6",
          secondaryTextClass: "text-[14px] leading-5",
          iconContainerClass: "w-8 h-8",
          iconClass: "h-5 w-5",
        };
      case "xl":
        return {
          noImageIconClass: "w-[27px] h-[27px]",
          initialsClass: "text-[20px] leading-6",
          containerClass: "w-16 h-16",
          primaryTextClass: "text-[18px] leading-6",
          secondaryTextClass: "text-[16px] leading-6",
          iconContainerClass: "w-10 h-10",
          iconClass: "h-6 w-6",
        };
      case "2xl":
        return {
          noImageIconClass: "w-[40px] h-[40px]",
          initialsClass: "text-[32px] leading-8",
          containerClass: "w-20 h-20",
          primaryTextClass: "text-[32px] leading-8",
          secondaryTextClass: "text-[24px] leading-8",
          iconContainerClass: "w-16 h-16",
          iconClass: "h-8 w-8",
        };
    }
  }, [size]);

  return (
    <div className="flex items-start gap-4 relative">
      {/*
        VERA-304: the grey plate + ring are the FALLBACK's surface, not the avatar's
        frame, so they are only drawn when there is no image.

        With a photo present the container painted `bg-gray-200` behind an image that
        covers it, and a `border-gray-300` ring around it — the image is sized to
        `containerClass` while the container is that size PLUS the border, so a grey
        hairline showed on every real avatar. Reported as "if a user has an avatar,
        don't show the containing grey box". Nothing is lost: a photograph bounds
        itself, and the initials/glyph cases keep the plate they need to be legible.

        `interactive` hover still applies to the fallback only, which is where a
        border exists to darken.
      */}
      <div
        className={cn(
          "flex items-center justify-center rounded-full",
          !src && "bg-gray-200 border-solid border-[1px] border-gray-300",
          !src && interactive && "hover:border-gray-400",
          containerClass
        )}
      >
        {src ? (
          <img
            src={src}
            alt="avatar"
            className={cn("rounded-full object-cover", containerClass)}
          />
        ) : initials ? (
          <div className="flex items-center justify-center">
            <p
              className={cn(
                initialsClass,
                "font-normal text-gray-500",
                interactive && "hover:text-gray-700"
              )}
            >
              {initials.slice(0, 2).toUpperCase()}
            </p>
          </div>
        ) : noImageIcon === "profile" ? (
          <UserRoundIcon
            className={cn(
              "text-gray-500",
              interactive && "hover:text-gray-700",
              noImageIconClass
            )}
          />
        ) : noImageIcon === "company" ? (
          <Building2
            className={cn(
              "text-gray-500",
              interactive && "hover:text-gray-700",
              noImageIconClass
            )}
          />
        ) : (
          <ImageIcon
            className={cn(
              "text-gray-500",
              interactive && "hover:text-gray-700",
              noImageIconClass
            )}
          />
        )}
      </div>
      {primaryText && (
        <div>
          {primaryText && (
            <p className={cn(primaryTextClass, "font-normal")}>{primaryText}</p>
          )}
          {/* VERA-304: was `text-secondary`, which is Tailwind's BACKGROUND token
              (`--secondary: 0 0% 96.1%` = #F5F5F5) being used as a text colour — so
              the line rendered near-white on white, ~1.1:1 contrast, effectively
              invisible. The matching text token would be `secondary-foreground`, but
              `text-gray-500` is what every other muted element in this component
              already uses (the initials, all three `noImageIcon` glyphs), so it keeps
              one muted tone rather than introducing a second. */}
          {secondaryText && (
            <p className={cn(secondaryTextClass, "font-normal text-gray-500")}>
              {secondaryText}
            </p>
          )}
          {descriptionText && (
            <p
              className={cn(
                secondaryTextClass,
                "mt-2 font-normal text-gray-700"
              )}
            >
              {descriptionText}
            </p>
          )}
        </div>
      )}
      {!!Icon && (
        <div
          className={cn(
            "absolute left-2 top-8 flex items-center justify-center rounded-md bg-gray-200 border-solid border-gray-300 border-[1px]",
            iconContainerClass
          )}
        >
          {<Icon className={cn("text-gray-700", iconClass)} />}
        </div>
      )}
    </div>
  );
};
